// Per-turn token density over one trace's parsed conversation (the `turns`
// array parseApiRequest produces). Answers "where in this conversation did the
// token mass -- and therefore the cost -- concentrate", per turn, so the Lanes
// timeline can draw a density strip on its turn axis.
//
// Two tiers of honesty, computed together:
//
//   1. MASS (always available): each turn's token estimate from its block
//      text at ~4 chars/token. Deterministic, turn-exact, but an estimate --
//      the tokenizer is not consulted and images are a flat allowance.
//
//   2. COST (when the caller supplies measured spend as USD bands): a
//      calibrated attribution of real spend onto turns. The SHAPE is modeled
//      -- a turn's content is re-read by every later assistant turn (each one
//      is an API call whose context contains it), so early mass costs more
//      than the same mass late. The LEVEL is measured -- each dollar band is
//      scaled so the per-turn shares sum exactly to the supplied spend, which
//      also absorbs what the model cannot see (sub-agent calls, tokenizer
//      error, system-prompt duplication). Per-turn dollars are therefore est.
//      shares of a real total, never an independent estimate.
//
//      The shape assumes the spend covers the WHOLE conversation shown (every
//      turn entered the context once; every later assistant turn re-read it),
//      so the honest level is cumulative -- the session's spend through this
//      conversation -- not one run's. Callers own that choice and must label
//      the strip with whichever level they actually supplied; feeding a
//      single run's dollars here spreads them as if they had built the whole
//      conversation. Pricing (tokens x catalog rates, per provider/model)
//      happens upstream in tokenCost.js -- this module only distributes
//      dollars it is handed.
const CHARS_PER_TOKEN = 4;
// Flat allowance per image block; Anthropic bills roughly (w*h)/750 tokens and
// the parsed block does not retain dimensions.
const IMAGE_TOKEN_EST = 1000;

/**
 * Characters one parsed block contributes to the conversation. Exported for
 * traceLaneWeight.js, which splits the same mass per SUBJECT instead of per
 * turn: both readings have to count a block identically or the per-lane shares
 * would not sum back to the per-turn bars.
 */
export function blockChars(block) {
    if (!block || typeof block !== "object") return 0;
    switch (block.type) {
        case "text":
            return String(block.text || "").length;
        case "thinking":
            return String(block.thinking || "").length;
        case "tool_use": {
            let n = String(block.name || "").length;
            const input = block.input;
            if (typeof input === "string") n += input.length;
            else if (input != null) {
                try {
                    n += JSON.stringify(input).length;
                } catch {
                    // circular / unserializable input: count nothing rather than throw
                }
            }
            return n;
        }
        case "tool_result": {
            const c = block.content;
            if (typeof c === "string") return c.length;
            if (Array.isArray(c)) {
                let n = 0;
                for (const b of c) {
                    if (b && b.type === "text" && typeof b.text === "string") n += b.text.length;
                    else if (b && b.type === "image") n += IMAGE_TOKEN_EST * CHARS_PER_TOKEN;
                }
                return n;
            }
            if (c != null) {
                try {
                    return JSON.stringify(c).length;
                } catch {
                    return 0;
                }
            }
            return 0;
        }
        case "image":
            return IMAGE_TOKEN_EST * CHARS_PER_TOKEN;
        case "unknown":
            return String(block.raw || "").length;
        default:
            return 0;
    }
}

/** Estimated tokens one parsed turn adds to the conversation. */
export function estimateTurnTokens(turn) {
    const blocks = turn?.blocks;
    if (!Array.isArray(blocks)) return 0;
    let chars = 0;
    for (const b of blocks) chars += blockChars(b);
    return Math.ceil(chars / CHARS_PER_TOKEN);
}

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Build the per-turn density model.
 *
 * `bandUsd` is measured spend in DOLLARS per band ({ input, output,
 * cacheRead, cacheWrite }, absent/null fields tolerated) -- the sumCostBands
 * / applyTokenRates shape from tokenCost.js. Without it (or with nothing
 * measured) the model is mass-only (`costed: false`, cost null).
 *
 * Returns null when there is nothing to measure (no turns / all-empty turns).
 */
export function buildTurnDensity(turns, { bandUsd = null } = {}) {
    if (!Array.isArray(turns) || turns.length === 0) return null;

    const est = turns.map((t) => estimateTurnTokens(t));
    const totalEst = est.reduce((a, b) => a + b, 0);
    if (totalEst <= 0) return null;

    // Re-read weight: how many later assistant turns carried this turn in
    // their request context (the main-thread call count after it). Suffix
    // count in one reverse pass.
    const readsAfter = new Array(turns.length).fill(0);
    let assistantSeen = 0;
    let assistantEst = 0;
    for (let i = turns.length - 1; i >= 0; i--) {
        readsAfter[i] = assistantSeen;
        if (turns[i]?.role === "assistant") {
            assistantSeen += 1;
            assistantEst += est[i];
        }
    }

    const usd = bandUsd
        ? {
            input: num(bandUsd.input),
            output: num(bandUsd.output),
            cacheRead: num(bandUsd.cacheRead),
            cacheWrite: num(bandUsd.cacheWrite),
        }
        : null;
    // A measured all-zero spend prices nothing -- render mass-only rather
    // than an "est. share of $0.00" that reads as free.
    const costed = Boolean(usd && usd.input + usd.output + usd.cacheRead + usd.cacheWrite > 0);

    let cost = null;
    let totalCost = null;
    if (costed) {
        // Three dollar pools, each distributed over turns by its own share
        // function and each summing exactly to its measured band:
        //   re-read pool  -- cache reads, by est * readsAfter (position-weighted)
        //   entry pool    -- cache writes + fresh input: content enters the
        //                    context once, by est share
        //   output pool   -- output dollars, by est share over assistant turns
        // Degenerate shapes (no assistant turns) fall back to plain est share
        // so a pool is never silently dropped.
        const readMass = est.map((e, i) => e * readsAfter[i]);
        const totalReadMass = readMass.reduce((a, b) => a + b, 0);
        const readPool = usd.cacheRead;
        const entryPool = usd.cacheWrite + usd.input;
        const outPool = usd.output;
        cost = est.map((e, i) => {
            const readShare = totalReadMass > 0 ? readMass[i] / totalReadMass : e / totalEst;
            const outShare = assistantEst > 0
                ? (turns[i]?.role === "assistant" ? e / assistantEst : 0)
                : e / totalEst;
            return readPool * readShare + entryPool * (e / totalEst) + outPool * outShare;
        });
        totalCost = readPool + entryPool + outPool;
    }

    let cum = 0;
    let cumCost = 0;
    const perTurn = est.map((e, i) => {
        cum += e;
        if (cost) cumCost += cost[i];
        return {
            index: i,
            role: turns[i]?.role || "",
            est: e,
            cum,
            // Later assistant turns whose request context carried this turn --
            // the concrete count behind the re-read weighting, for tooltips.
            reads: readsAfter[i],
            cost: cost ? cost[i] : null,
            // Running spend attributed through this turn (null when un-costed).
            cumCost: cost ? cumCost : null,
        };
    });

    return { turns: perTurn, totalEst, costed, totalCost };
}
